/**
 * Copyright Highway9 Networks Inc.
 *
 * Client for the prompt category API at /api/v1/saas/ai-assistant/prompt-categories.
 */

import type { AssistantPromptCategory } from "@h9/ui-lib/components/organisms/AiAssistant";
import { authenticationService } from "./authentication-service";

const REST_BASE = "/api/v1/saas/ai-assistant/prompt-categories";

export type { AssistantPromptCategory };

export type ObjectReference = {
  id?: string;
  name?: string;
};

const authHeader = () => ({
  Authorization: `Bearer ${authenticationService?.currentUserValue?.token ?? ""}`,
});

export const zivaPromptCategoryService = {
  async list(): Promise<AssistantPromptCategory[]> {
    const r = await fetch(REST_BASE, { headers: { ...authHeader() } });
    if (!r.ok) {
      throw new Error(`Failed to load prompt categories (${r.status})`);
    }
    const text = await r.text();
    if (!text.trim()) return [];
    try {
      const body = JSON.parse(text) as { data?: AssistantPromptCategory[] };
      return body?.data ?? [];
    } catch {
      return [];
    }
  },

  async get(id: string): Promise<AssistantPromptCategory> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      headers: { ...authHeader() },
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "load prompt category"));
    }
    return (await r.json()) as AssistantPromptCategory;
  },

  async create(category: AssistantPromptCategory): Promise<AssistantPromptCategory> {
    const r = await fetch(REST_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(category),
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "create prompt category"));
    }
    return (await r.json()) as AssistantPromptCategory;
  },

  async update(id: string, category: AssistantPromptCategory): Promise<AssistantPromptCategory> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(category),
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "update prompt category"));
    }
    return (await r.json()) as AssistantPromptCategory;
  },

  async remove(id: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...authHeader() },
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "delete prompt category"));
    }
  },
};

async function extractError(r: Response, action: string): Promise<string> {
  try {
    const body = (await r.json()) as { errors?: { message?: string }[] };
    const msg = body?.errors?.[0]?.message;
    if (msg) return msg;
  } catch {
    // ignore parse errors
  }
  return `Failed to ${action} (${r.status})`;
}
